import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import Logo from "@/components/Logo";
import { useLang } from "@/i18n";

const EASE = [0.22, 1, 0.36, 1];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu whenever the route changes
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const links = [
    { to: "/", label: t("nav.home") },
    { to: "/dhamma", label: t("nav.dhamma") },
    { to: "/our-work", label: t("nav.ourwork") },
    { to: "/about/journey", label: t("nav.journey") },
    { to: "/media", label: t("nav.media") },
    { to: "/contact", label: t("nav.contact") },
  ];

  return (
    <header className={`sticky top-0 z-50 transition-colors duration-300 ${scrolled || open ? "navbar-scrolled" : "navbar-glass"}`}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[60] focus:bg-saffron focus:text-charcoal focus:px-4 focus:py-2 focus:rounded-md focus:font-semibold"
        data-testid="skip-to-content-link"
      >
        Skip to main content
      </a>
      <nav className="max-w-7xl mx-auto pl-5 pr-4 lg:pl-10 lg:pr-8 flex items-center justify-between py-[11px]" aria-label="Primary">
        <Link to="/" className="flex items-center gap-2.5 sm:gap-3 no-underline mr-4 lg:mr-10" data-testid="nav-home-logo-link" onClick={() => setOpen(false)}>
          <Logo height={36} testId="nav-logo-video" />
          <span className="flex flex-col leading-tight" data-testid="nav-wordmark">
            <span className="font-serif text-ivory text-[18px] sm:text-[19px] font-semibold tracking-[0.06em]">IBMCT</span>
            <span className="font-sans text-[8px] sm:text-[9px] italic font-medium uppercase tracking-[0.2em] sm:tracking-[0.22em] text-saffron-light/85 mt-[2px]">
              Atta Deepo Bhava
            </span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-2 xl:gap-3">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}
              end={l.to === "/"}
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/donate"
            className="ml-3 inline-flex items-center justify-center font-sans font-semibold text-[13.5px] tracking-[0.02em] rounded-full border border-saffron/70 text-saffron-light hover:bg-saffron hover:text-charcoal hover:border-saffron transition-colors duration-200"
            style={{ minHeight: 46, padding: "10px 24px" }}
            data-testid="nav-donate-button"
          >
            {t("nav.donate")}
          </Link>
        </div>

        <div className="lg:hidden flex items-center">
          <button
            type="button"
            className="text-ivory p-3 -mr-2 active:scale-90 transition-transform duration-150"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle-button"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.34, ease: EASE }}
            className="lg:hidden overflow-hidden bg-[#17120D]/98 backdrop-blur-md border-t border-[rgba(201,138,46,0.14)]"
            data-testid="mobile-menu"
          >
            <div className="px-5 pb-6 pt-2">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -14 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.06 + i * 0.05, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className={() =>
                      `nav-link !flex w-full !text-[16px] !py-4 border-b border-[rgba(245,239,228,0.08)] ${
                        location.pathname === l.to ? "nav-link-active" : ""
                      }`
                    }
                    onClick={() => setOpen(false)}
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.06 + links.length * 0.05, ease: EASE }}
              >
                <Link
                  to="/donate"
                  className="w-full mt-5 inline-flex items-center justify-center font-sans font-semibold text-[15px] rounded-full border border-saffron/70 text-saffron-light hover:bg-saffron hover:text-charcoal transition-colors duration-200"
                  style={{ minHeight: 52, padding: "13px 24px" }}
                  data-testid="mobile-nav-donate-button"
                  onClick={() => setOpen(false)}
                >
                  {t("nav.donate")}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
